import type { FC, ReactNode } from "react";
import { Paper, Table, Text, useMatches } from "@mantine/core";

import {
  COLUMN_VISIBLE_FROM,
  OrderSummaryRowV2,
} from "/@/components/order/OrderSummaryRowV2";
import type {
  MaterialAndShadeCatalog,
  OrdersListOrder,
} from "@jasper/use-cases";

type OrdersSummaryTableProps = {
  orders: OrdersListOrder[];
  // Names the materials and shades of the orders products
  materialAndShadeCatalog: MaterialAndShadeCatalog;
  emptyMessage: string;
  // Fades the rows while the next ones load.
  isDimmed?: boolean;
  // Rendered under the table, inside the card (pagination).
  footer?: ReactNode;
};

/*
  Orders table shared by « Mes commandes » and the dashboard. Its minimum width follows the
  columns hidden on narrower screens, and it scrolls horizontally below that width.
*/
export const OrdersSummaryTable: FC<OrdersSummaryTableProps> = ({
  orders,
  materialAndShadeCatalog,
  emptyMessage,
  isDimmed = false,
  footer,
}) => {
  const minWidth = useMatches({ base: 616, sm: 726, md: 806 });

  return (
    <Paper
      withBorder
      radius="lg"
      bg="cardBackground"
      style={{ overflow: "hidden" }}
    >
      <Table.ScrollContainer minWidth={minWidth} type="native">
        <Table layout="fixed">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={64} />
              <Table.Th>Commande</Table.Th>
              <Table.Th w={160}>Patient</Table.Th>
              <Table.Th w={80} visibleFrom={COLUMN_VISIBLE_FROM.date}>
                Date
              </Table.Th>
              <Table.Th w={140}>Statut</Table.Th>
              <Table.Th w={110} visibleFrom={COLUMN_VISIBLE_FROM.delivery}>
                Livraison
              </Table.Th>
              <Table.Th w={56} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody opacity={isDimmed ? 0.3 : 1}>
            {orders.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text variant="caption" ta="center" py="md">
                    {emptyMessage}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {orders.map(order => (
              <OrderSummaryRowV2
                key={order.id}
                order={order}
                materialAndShadeCatalog={materialAndShadeCatalog}
              />
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {footer}
    </Paper>
  );
};
